import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Bot, Database, RefreshCw, Save,
  Play, CheckCircle, XCircle, Clock,
  Copy, Eye, EyeOff, Plus, Trash2, Send, Settings,
  Loader2, Zap, Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Slider } from '../../components/ui/slider';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const WhatsAppAIPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  
  // Server state
  const [stats, setStats] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  
  // Conflicts state
  const [conflicts, setConflicts] = useState([]);
  const [conflictsTotal, setConflictsTotal] = useState(0);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  
  // Local form state for Webhook Config
  const [webhookForm, setWebhookForm] = useState({
    enabled: true,
    webhook_id: '',
    verify_token: '',
    whatsapp_token: '',
    has_whatsapp_token: false
  });
  
  // Local form state for AI Config
  const [aiForm, setAiForm] = useState({
    enabled: true,
    provider: 'openai',
    model_name: 'gpt-4o',
    api_key: '',
    has_api_key: false,
    use_emergent_key: true,
    temperature: 0.3,
    max_tokens: 1000,
    confidence_threshold: 70,
    product_description: '',
    company_name: '',
    system_prompt: '',
    available_providers: [],
    send_lead_creation_message: false,
    lead_creation_message_template: ''
  });
  
  // Local form state for Storage Config
  const [storageForm, setStorageForm] = useState({
    use_s3: false,
    s3_bucket: '',
    s3_region: '',
    s3_access_key: '',
    s3_secret_key: '',
    s3_endpoint_url: '',
    local_path: '/app/backend/media',
    has_s3_credentials: false
  });
  
  // WA Send Config state
  const [sendConfigs, setSendConfigs] = useState([]);
  const [sendConfigForm, setSendConfigForm] = useState({
    name: '',
    description: '',
    api_url: 'https://graph.facebook.com/v21.0/{phone_number_id}/messages',
    phone_number_id: '',
    access_token: '',
    headers: [{ key: '', value: '' }],  // Array of key-value pairs
    enable_ai_response: false,
    ai_response_delay_seconds: 2,
    is_default: true
  });
  const [editingSendConfig, setEditingSendConfig] = useState(null);
  const [showSendConfigDialog, setShowSendConfigDialog] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  
  // Test AI state
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  
  // UI state
  const [showApiKey, setShowApiKey] = useState(false);
  const [showS3Key, setShowS3Key] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchConflicts = async () => {
    setConflictsLoading(true);
    try {
      const res = await api.get('/api/whatsapp-ai/conflicts?limit=50');
      setConflicts(res.data.conflicts || []);
      setConflictsTotal(res.data.total || 0);
      // Update stats with pending conflicts count
      setStats(prev => ({ ...prev, pending_conflicts: res.data.pending_count || 0 }));
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to fetch conflicts' });
    } finally {
      setConflictsLoading(false);
    }
  };

  const resolveConflict = async (conflictId, resolution, notes = '') => {
    try {
      await api.patch(`/api/whatsapp-ai/conflicts/${conflictId}/resolve`, {
        resolution,
        notes
      });
      toast({ title: 'Conflict resolved' });
      fetchConflicts();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to resolve conflict' });
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [webhookRes, aiRes, storageRes, statsRes, webhooksRes, conflictsRes] = await Promise.all([
        api.get('/api/whatsapp-ai/webhook/config'),
        api.get('/api/whatsapp-ai/ai/config'),
        api.get('/api/whatsapp-ai/storage/config'),
        api.get('/api/whatsapp-ai/stats'),
        api.get('/api/whatsapp-ai/webhooks?limit=20'),
        api.get('/api/whatsapp-ai/conflicts?limit=50')
      ]);
      
      // Set conflicts
      setConflicts(conflictsRes.data.conflicts || []);
      setConflictsTotal(conflictsRes.data.total || 0);
      
      // Populate form states
      setWebhookForm({
        enabled: webhookRes.data.enabled,
        webhook_id: webhookRes.data.webhook_id || '',
        verify_token: webhookRes.data.verify_token || '',
        whatsapp_token: '',
        has_whatsapp_token: webhookRes.data.has_whatsapp_token
      });
      
      setAiForm({
        enabled: aiRes.data.enabled,
        provider: aiRes.data.provider || 'openai',
        model_name: aiRes.data.model_name || 'gpt-4o',
        api_key: '',
        has_api_key: aiRes.data.has_api_key,
        use_emergent_key: aiRes.data.use_emergent_key,
        temperature: aiRes.data.temperature || 0.3,
        max_tokens: aiRes.data.max_tokens || 1000,
        confidence_threshold: aiRes.data.confidence_threshold || 70,
        product_description: aiRes.data.product_description || '',
        company_name: aiRes.data.company_name || '',
        system_prompt: aiRes.data.system_prompt || '',
        available_providers: aiRes.data.available_providers || [],
        send_lead_creation_message: aiRes.data.send_lead_creation_message || false,
        lead_creation_message_template: aiRes.data.lead_creation_message_template || ''
      });
      
      setStorageForm({
        use_s3: storageRes.data.use_s3,
        s3_bucket: storageRes.data.s3_bucket || '',
        s3_region: storageRes.data.s3_region || '',
        s3_access_key: '',
        s3_secret_key: '',
        s3_endpoint_url: storageRes.data.s3_endpoint_url || '',
        local_path: storageRes.data.local_path || '/app/backend/media',
        has_s3_credentials: storageRes.data.has_s3_credentials
      });
      
      setStats({
        ...statsRes.data,
        pending_conflicts: conflictsRes.data.pending_count || 0
      });
      setWebhooks(webhooksRes.data.items || []);
      
      // Fetch send configs
      try {
        const sendConfigRes = await api.get('/api/whatsapp-ai/send-config');
        setSendConfigs(sendConfigRes.data.configs || []);
      } catch (e) {
        console.log('Send config not available yet');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast({ variant: 'destructive', title: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  // Send Config handlers
  const fetchSendConfigs = async () => {
    try {
      const res = await api.get('/api/whatsapp-ai/send-config');
      setSendConfigs(res.data.configs || []);
    } catch (error) {
      console.error('Failed to fetch send configs:', error);
    }
  };

  const saveSendConfig = async () => {
    setSaving(s => ({ ...s, sendConfig: true }));
    try {
      // Convert headers array to object
      const customHeaders = {};
      sendConfigForm.headers.forEach(h => {
        if (h.key && h.key.trim()) {
          customHeaders[h.key.trim()] = h.value || '';
        }
      });

      const data = {
        name: sendConfigForm.name,
        description: sendConfigForm.description,
        api_url: sendConfigForm.api_url,
        phone_number_id: sendConfigForm.phone_number_id,
        access_token: sendConfigForm.access_token || undefined,
        custom_headers: Object.keys(customHeaders).length > 0 ? customHeaders : null,
        enable_ai_response: sendConfigForm.enable_ai_response,
        ai_response_delay_seconds: sendConfigForm.ai_response_delay_seconds,
        is_default: sendConfigForm.is_default
      };

      if (editingSendConfig) {
        await api.patch(`/api/whatsapp-ai/send-config/${editingSendConfig}`, data);
        toast({ title: 'Send configuration updated' });
      } else {
        await api.post('/api/whatsapp-ai/send-config', data);
        toast({ title: 'Send configuration created' });
      }

      setShowSendConfigDialog(false);
      setEditingSendConfig(null);
      resetSendConfigForm();
      fetchSendConfigs();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to save send config', description: error.response?.data?.detail });
    } finally {
      setSaving(s => ({ ...s, sendConfig: false }));
    }
  };

  const deleteSendConfig = async (configId) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;
    try {
      await api.delete(`/api/whatsapp-ai/send-config/${configId}`);
      toast({ title: 'Configuration deleted' });
      fetchSendConfigs();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to delete config' });
    }
  };

  const editSendConfig = (config) => {
    // Convert custom_headers object to array format
    const headersObj = config.custom_headers || {};
    const headersArray = Object.entries(headersObj).map(([key, value]) => ({ key, value }));
    if (headersArray.length === 0) {
      headersArray.push({ key: '', value: '' });
    }
    
    setSendConfigForm({
      name: config.name || '',
      description: config.description || '',
      api_url: config.api_url || '',
      phone_number_id: config.phone_number_id || '',
      access_token: '',
      headers: headersArray,
      enable_ai_response: config.enable_ai_response || false,
      ai_response_delay_seconds: config.ai_response_delay_seconds || 2,
      is_default: config.is_default || false
    });
    setEditingSendConfig(config.id);
    setShowSendConfigDialog(true);
  };

  const resetSendConfigForm = () => {
    setSendConfigForm({
      name: '',
      description: '',
      api_url: 'https://graph.facebook.com/v21.0/{phone_number_id}/messages',
      phone_number_id: '',
      access_token: '',
      headers: [{ key: '', value: '' }],
      enable_ai_response: false,
      ai_response_delay_seconds: 2,
      is_default: true
    });
  };

  const addHeader = () => {
    setSendConfigForm(f => ({
      ...f,
      headers: [...f.headers, { key: '', value: '' }]
    }));
  };

  const removeHeader = (index) => {
    setSendConfigForm(f => ({
      ...f,
      headers: f.headers.filter((_, i) => i !== index)
    }));
  };

  const updateHeader = (index, field, value) => {
    setSendConfigForm(f => ({
      ...f,
      headers: f.headers.map((h, i) => i === index ? { ...h, [field]: value } : h)
    }));
  };

  // Save handlers - only called on button click
  const saveWebhookConfig = async () => {
    setSaving(s => ({ ...s, webhook: true }));
    try {
      const data = {};
      if (webhookForm.verify_token !== undefined) data.verify_token = webhookForm.verify_token;
      if (webhookForm.whatsapp_token) data.whatsapp_token = webhookForm.whatsapp_token;
      if (webhookForm.enabled !== undefined) data.enabled = webhookForm.enabled;
      
      await api.patch('/api/whatsapp-ai/webhook/config', data);
      toast({ title: 'Webhook configuration saved' });
      // Update has_whatsapp_token if token was set
      if (webhookForm.whatsapp_token) {
        setWebhookForm(f => ({ ...f, has_whatsapp_token: true, whatsapp_token: '' }));
      }
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to save webhook config' });
    } finally {
      setSaving(s => ({ ...s, webhook: false }));
    }
  };

  const saveAIConfig = async () => {
    setSaving(s => ({ ...s, ai: true }));
    try {
      const data = {
        enabled: aiForm.enabled,
        provider: aiForm.provider,
        model_name: aiForm.model_name,
        use_emergent_key: aiForm.use_emergent_key,
        temperature: aiForm.temperature,
        max_tokens: aiForm.max_tokens,
        confidence_threshold: aiForm.confidence_threshold,
        product_description: aiForm.product_description,
        company_name: aiForm.company_name,
        system_prompt: aiForm.system_prompt,
        send_lead_creation_message: aiForm.send_lead_creation_message,
        lead_creation_message_template: aiForm.lead_creation_message_template
      };
      if (aiForm.api_key) data.api_key = aiForm.api_key;
      
      await api.patch('/api/whatsapp-ai/ai/config', data);
      toast({ title: 'AI configuration saved' });
      if (aiForm.api_key) {
        setAiForm(f => ({ ...f, has_api_key: true, api_key: '' }));
      }
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to save AI config' });
    } finally {
      setSaving(s => ({ ...s, ai: false }));
    }
  };

  const saveStorageConfig = async () => {
    setSaving(s => ({ ...s, storage: true }));
    try {
      const data = {
        use_s3: storageForm.use_s3,
        s3_bucket: storageForm.s3_bucket,
        s3_region: storageForm.s3_region,
        s3_endpoint_url: storageForm.s3_endpoint_url,
        local_path: storageForm.local_path
      };
      if (storageForm.s3_access_key) data.s3_access_key = storageForm.s3_access_key;
      if (storageForm.s3_secret_key) data.s3_secret_key = storageForm.s3_secret_key;
      
      await api.patch('/api/whatsapp-ai/storage/config', data);
      toast({ title: 'Storage configuration saved' });
      if (storageForm.s3_access_key || storageForm.s3_secret_key) {
        setStorageForm(f => ({ ...f, has_s3_credentials: true, s3_access_key: '', s3_secret_key: '' }));
      }
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to save storage config' });
    } finally {
      setSaving(s => ({ ...s, storage: false }));
    }
  };

  const regenerateWebhookId = async () => {
    setSaving(s => ({ ...s, regenerate: true }));
    try {
      const res = await api.post('/api/whatsapp-ai/webhook/regenerate');
      setWebhookForm(f => ({ ...f, webhook_id: res.data.webhook_id }));
      toast({ title: 'Webhook ID regenerated' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to regenerate webhook ID' });
    } finally {
      setSaving(s => ({ ...s, regenerate: false }));
    }
  };

  const testAI = async () => {
    if (!testMessage.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/api/whatsapp-ai/ai/test', { message: testMessage });
      setTestResult(res.data);
    } catch (error) {
      toast({ variant: 'destructive', title: 'AI test failed' });
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard' });
  };

  const viewWebhookDetail = async (webhookId) => {
    try {
      const res = await api.get(`/api/whatsapp-ai/webhooks/${webhookId}`);
      setSelectedWebhook(res.data);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to load webhook details' });
    }
  };

  const reprocessWebhook = async (webhookId) => {
    try {
      await api.post(`/api/whatsapp-ai/webhooks/${webhookId}/reprocess`);
      toast({ title: 'Webhook queued for reprocessing' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to reprocess webhook' });
    }
  };

  const refreshWebhooks = async () => {
    try {
      const [statsRes, webhooksRes] = await Promise.all([
        api.get('/api/whatsapp-ai/stats'),
        api.get('/api/whatsapp-ai/webhooks?limit=20')
      ]);
      setStats(statsRes.data);
      setWebhooks(webhooksRes.data.items || []);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to refresh' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading WhatsApp AI configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-emerald-600" />
            WhatsApp AI Lead Analyzer
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Configure AI-powered lead qualification from WhatsApp messages
          </p>
        </div>
        <Button onClick={fetchAllData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh All
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-800">{stats?.total_webhooks || 0}</div>
            <div className="text-sm text-slate-500">Total Messages</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-600">{stats?.pending || 0}</div>
            <div className="text-sm text-slate-500">Pending</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-emerald-600">{stats?.completed || 0}</div>
            <div className="text-sm text-slate-500">Processed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{stats?.failed || 0}</div>
            <div className="text-sm text-slate-500">Failed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats?.leads_identified || 0}</div>
            <div className="text-sm text-slate-500">Leads Found</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600">{stats?.avg_lead_confidence || 0}%</div>
            <div className="text-sm text-slate-500">Avg Confidence</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="webhook" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="webhook">Webhook Config</TabsTrigger>
          <TabsTrigger value="ai">AI Settings</TabsTrigger>
          <TabsTrigger value="send-config">
            <Send className="w-4 h-4 mr-1" />
            Send Config
          </TabsTrigger>
          <TabsTrigger value="storage">Storage</TabsTrigger>
          <TabsTrigger value="stream">Message Stream</TabsTrigger>
          <TabsTrigger value="conflicts" className="relative">
            Conflicts
            {stats?.pending_conflicts > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                {stats.pending_conflicts}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="test">Test AI</TabsTrigger>
        </TabsList>

        {/* Webhook Config Tab */}
        <TabsContent value="webhook">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Configuration</CardTitle>
              <CardDescription>Configure the WhatsApp webhook endpoint</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Webhook Enabled</Label>
                  <p className="text-sm text-slate-500">Receive and process WhatsApp messages</p>
                </div>
                <Switch
                  checked={webhookForm.enabled}
                  onCheckedChange={(v) => setWebhookForm(f => ({ ...f, enabled: v }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={`${window.location.origin}/api/webhook/whatsapp/${webhookForm.webhook_id}`}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(`${window.location.origin}/api/webhook/whatsapp/${webhookForm.webhook_id}`)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Use this URL in your WhatsApp Business API configuration</p>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={regenerateWebhookId} disabled={saving.regenerate}>
                  {saving.regenerate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Regenerate Webhook ID
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Verify Token (Optional)</Label>
                <Input
                  value={webhookForm.verify_token}
                  onChange={(e) => setWebhookForm(f => ({ ...f, verify_token: e.target.value }))}
                  placeholder="Enter verification token for Meta webhook verification"
                />
              </div>

              <div className="space-y-2">
                <Label>WhatsApp Business API Token</Label>
                <div className="flex gap-2">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={webhookForm.whatsapp_token}
                    onChange={(e) => setWebhookForm(f => ({ ...f, whatsapp_token: e.target.value }))}
                    placeholder={webhookForm.has_whatsapp_token ? '••••••••••••' : 'Enter WhatsApp API token'}
                  />
                  <Button variant="outline" onClick={() => setShowApiKey(!showApiKey)}>
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Required for downloading media files from WhatsApp</p>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveWebhookConfig} disabled={saving.webhook}>
                  {saving.webhook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Webhook Config
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Settings Tab */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-purple-600" />
                AI Configuration
              </CardTitle>
              <CardDescription>Configure the AI model for lead qualification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div>
                  <Label className="text-base font-medium">AI Analysis Enabled</Label>
                  <p className="text-sm text-slate-500">Process messages with AI for lead qualification</p>
                </div>
                <Switch
                  checked={aiForm.enabled}
                  onCheckedChange={(v) => setAiForm(f => ({ ...f, enabled: v }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>AI Provider</Label>
                  <Select
                    value={aiForm.provider}
                    onValueChange={(v) => setAiForm(f => ({ ...f, provider: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {aiForm.available_providers?.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select
                    value={aiForm.model_name}
                    onValueChange={(v) => setAiForm(f => ({ ...f, model_name: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {aiForm.available_providers
                        ?.find((p) => p.id === aiForm.provider)
                        ?.models?.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div>
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    Use Emergent Universal Key
                  </Label>
                  <p className="text-sm text-slate-500">Use pre-configured API key (credits deducted from your balance)</p>
                </div>
                <Switch
                  checked={aiForm.use_emergent_key}
                  onCheckedChange={(v) => setAiForm(f => ({ ...f, use_emergent_key: v }))}
                />
              </div>

              {!aiForm.use_emergent_key && (
                <div className="space-y-2">
                  <Label>Your API Key</Label>
                  <Input
                    type="password"
                    value={aiForm.api_key}
                    onChange={(e) => setAiForm(f => ({ ...f, api_key: e.target.value }))}
                    placeholder={aiForm.has_api_key ? '••••••••••••' : 'Enter your API key'}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Temperature: {aiForm.temperature}</Label>
                <Slider
                  value={[aiForm.temperature * 100]}
                  max={100}
                  step={10}
                  onValueChange={(v) => setAiForm(f => ({ ...f, temperature: v[0] / 100 }))}
                />
                <p className="text-xs text-slate-500">Lower = more deterministic, Higher = more creative</p>
              </div>

              <div className="space-y-2">
                <Label>Lead Confidence Threshold: {aiForm.confidence_threshold}%</Label>
                <Slider
                  value={[aiForm.confidence_threshold]}
                  max={100}
                  step={5}
                  onValueChange={(v) => setAiForm(f => ({ ...f, confidence_threshold: v[0] }))}
                />
                <p className="text-xs text-slate-500">Minimum confidence required to create a lead</p>
              </div>

              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={aiForm.company_name}
                  onChange={(e) => setAiForm(f => ({ ...f, company_name: e.target.value }))}
                  placeholder="Your company name"
                />
              </div>

              <div className="space-y-2">
                <Label>Product/Service Description</Label>
                <Textarea
                  value={aiForm.product_description}
                  onChange={(e) => setAiForm(f => ({ ...f, product_description: e.target.value }))}
                  placeholder="Describe your products/services to help AI understand lead context..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Custom System Prompt (Optional)</Label>
                <Textarea
                  value={aiForm.system_prompt}
                  onChange={(e) => setAiForm(f => ({ ...f, system_prompt: e.target.value }))}
                  placeholder="Leave empty to use default prompt. Use {product_context} placeholder for product description."
                  rows={6}
                />
              </div>

              {/* Lead Creation Message Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4 text-emerald-600" />
                  Lead Creation Auto-Response
                </h4>
                <p className="text-sm text-slate-500 mb-4">
                  Automatically send a message to the customer when a new lead is created from their WhatsApp message.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Send message on lead creation</Label>
                      <p className="text-xs text-slate-500">When enabled, a message will be sent to the customer automatically</p>
                    </div>
                    <Switch
                      data-testid="send-lead-creation-message-toggle"
                      checked={aiForm.send_lead_creation_message}
                      onCheckedChange={(v) => setAiForm(f => ({ ...f, send_lead_creation_message: v }))}
                    />
                  </div>
                  
                  {aiForm.send_lead_creation_message && (
                    <div>
                      <Label>Message Template</Label>
                      <Textarea
                        data-testid="lead-creation-message-template"
                        value={aiForm.lead_creation_message_template}
                        onChange={(e) => setAiForm(f => ({ ...f, lead_creation_message_template: e.target.value }))}
                        placeholder="Hi {name}, thank you for reaching out! We have received your inquiry and our team will contact you shortly."
                        rows={3}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Available placeholders: <code className="bg-slate-100 px-1 rounded">{'{name}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{company}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{phone}'}</code>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button data-testid="save-ai-config-btn" onClick={saveAIConfig} disabled={saving.ai}>
                  {saving.ai ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save AI Config
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Storage Tab */}
        <TabsContent value="storage">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                Storage Configuration
              </CardTitle>
              <CardDescription>Configure media file storage (audio, images, documents)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Use S3 Storage</Label>
                  <p className="text-sm text-slate-500">Store media files in AWS S3 (fallback to local if not configured)</p>
                </div>
                <Switch
                  checked={storageForm.use_s3}
                  onCheckedChange={(v) => setStorageForm(f => ({ ...f, use_s3: v }))}
                />
              </div>

              {storageForm.use_s3 && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>S3 Bucket</Label>
                      <Input
                        value={storageForm.s3_bucket}
                        onChange={(e) => setStorageForm(f => ({ ...f, s3_bucket: e.target.value }))}
                        placeholder="my-bucket-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>AWS Region</Label>
                      <Input
                        value={storageForm.s3_region}
                        onChange={(e) => setStorageForm(f => ({ ...f, s3_region: e.target.value }))}
                        placeholder="us-east-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>S3 Access Key</Label>
                    <div className="flex gap-2">
                      <Input
                        type={showS3Key ? 'text' : 'password'}
                        value={storageForm.s3_access_key}
                        onChange={(e) => setStorageForm(f => ({ ...f, s3_access_key: e.target.value }))}
                        placeholder={storageForm.has_s3_credentials ? '••••••••••••' : 'AWS Access Key ID'}
                      />
                      <Button variant="outline" onClick={() => setShowS3Key(!showS3Key)}>
                        {showS3Key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>S3 Secret Key</Label>
                    <Input
                      type="password"
                      value={storageForm.s3_secret_key}
                      onChange={(e) => setStorageForm(f => ({ ...f, s3_secret_key: e.target.value }))}
                      placeholder="AWS Secret Access Key"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Custom S3 Endpoint (Optional)</Label>
                    <Input
                      value={storageForm.s3_endpoint_url}
                      onChange={(e) => setStorageForm(f => ({ ...f, s3_endpoint_url: e.target.value }))}
                      placeholder="https://s3.custom-endpoint.com (for S3-compatible storage)"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Local Storage Path</Label>
                <Input
                  value={storageForm.local_path}
                  onChange={(e) => setStorageForm(f => ({ ...f, local_path: e.target.value }))}
                  placeholder="/app/backend/media"
                />
                <p className="text-xs text-slate-500">Used when S3 is not configured or as fallback</p>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveStorageConfig} disabled={saving.storage}>
                  {saving.storage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Storage Config
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Send Config Tab - WhatsApp Partner API Configuration */}
        <TabsContent value="send-config">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  WhatsApp Send Configuration
                </CardTitle>
                <CardDescription>
                  Configure your WhatsApp Business API endpoint for sending AI responses.
                  This follows Meta Cloud API format.
                </CardDescription>
              </div>
              <Button onClick={() => { resetSendConfigForm(); setEditingSendConfig(null); setShowSendConfigDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Config
              </Button>
            </CardHeader>
            <CardContent>
              {sendConfigs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Send className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No send configurations yet.</p>
                  <p className="text-sm mt-1">Add a configuration to enable AI auto-responses via WhatsApp.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sendConfigs.map((config) => (
                    <div key={config.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{config.name}</h4>
                          {config.is_default && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700">Default</Badge>
                          )}
                          {config.is_active ? (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700">Active</Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-500">Inactive</Badge>
                          )}
                          {config.enable_ai_response && (
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700">AI Response ON</Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => editSendConfig(config)}>
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteSendConfig(config.id)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {config.description && (
                        <p className="text-sm text-slate-500">{config.description}</p>
                      )}
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">API URL:</span>
                          <code className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded break-all">{config.api_url}</code>
                        </div>
                        <div>
                          <span className="text-slate-500">Phone Number ID:</span>
                          <code className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded">{config.phone_number_id || 'Not set'}</code>
                        </div>
                        <div>
                          <span className="text-slate-500">Access Token:</span>
                          <span className="ml-2">{config.access_token ? '✅ Configured' : '❌ Not set'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">AI Response Delay:</span>
                          <span className="ml-2">{config.ai_response_delay_seconds || 2}s</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center pt-2 border-t text-xs text-slate-400">
                        <span>Sent: {config.total_messages_sent || 0} | Success: {config.successful_sends || 0} | Failed: {config.failed_sends || 0}</span>
                        <span>Created: {new Date(config.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Info Box */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">📘 Meta WhatsApp Business API Format</h4>
                <div className="text-sm text-blue-700 space-y-2">
                  <p><strong>API URL Format:</strong></p>
                  <code className="block bg-white p-2 rounded text-xs">
                    https://graph.facebook.com/v21.0/&#123;PHONE_NUMBER_ID&#125;/messages
                  </code>
                  <p className="mt-2"><strong>Required Headers:</strong></p>
                  <code className="block bg-white p-2 rounded text-xs whitespace-pre">
{`{
  "Authorization": "Bearer YOUR_ACCESS_TOKEN",
  "Content-Type": "application/json"
}`}
                  </code>
                  <p className="mt-2 text-xs">
                    Get your Phone Number ID and Access Token from the 
                    <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="underline ml-1">Meta Developer Console</a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Message Stream Tab */}
        <TabsContent value="stream">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Incoming Messages</CardTitle>
                <CardDescription>View and manage incoming WhatsApp webhook messages</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={refreshWebhooks}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {webhooks.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No messages received yet</p>
                    <p className="text-sm">Configure your WhatsApp webhook to start receiving messages</p>
                  </div>
                ) : (
                  webhooks.map((w) => (
                    <div
                      key={w.id}
                      className="p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer"
                      onClick={() => viewWebhookDetail(w.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge className={
                            w.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            w.status === 'failed' ? 'bg-red-100 text-red-700' :
                            w.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }>
                            {w.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {w.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                            {w.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {w.status === 'processing' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                            {w.status}
                          </Badge>
                          <Badge variant="outline">{w.message_type}</Badge>
                          <span className="text-sm font-mono text-slate-600">{w.phone}</span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(w.received_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-700 truncate">{w.message_preview}</p>
                      {w.error && (
                        <p className="mt-1 text-xs text-red-600 truncate">{w.error}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test AI Tab */}
        <TabsContent value="test">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-600" />
                Test AI Analysis
              </CardTitle>
              <CardDescription>Test the AI lead qualification with a sample message</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Sample Message</Label>
                <Textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="Enter a sample customer message to test AI analysis..."
                  rows={4}
                />
              </div>

              <Button onClick={testAI} disabled={testing || !testMessage.trim()}>
                {testing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Run AI Analysis
                  </>
                )}
              </Button>

              {testResult && (
                <div className="mt-4 p-4 border rounded-lg space-y-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <Badge className={testResult.is_lead ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                      {testResult.is_lead ? 'LEAD' : 'NOT A LEAD'}
                    </Badge>
                    <span className="text-sm">
                      Confidence: <strong>{testResult.confidence}%</strong>
                    </span>
                    <span className="text-sm text-slate-500">
                      {testResult.processing_time_ms}ms
                    </span>
                  </div>

                  {/* Token Usage for Test */}
                  {(testResult.total_tokens || testResult.input_tokens || testResult.output_tokens) && (
                    <div className="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                      <Zap className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-700 dark:text-blue-300">Tokens:</span>
                      {testResult.input_tokens && (
                        <span className="text-blue-600">In: <strong>{testResult.input_tokens}</strong></span>
                      )}
                      {testResult.output_tokens && (
                        <span className="text-blue-600">Out: <strong>{testResult.output_tokens}</strong></span>
                      )}
                      {testResult.total_tokens && (
                        <span className="text-blue-700 font-medium">Total: <strong>{testResult.total_tokens}</strong></span>
                      )}
                    </div>
                  )}

                  {testResult.reason && (
                    <div>
                      <Label className="text-xs text-slate-500">Reason</Label>
                      <p className="text-sm">{testResult.reason}</p>
                    </div>
                  )}

                  {testResult.intent && (
                    <div>
                      <Label className="text-xs text-slate-500">Detected Intent</Label>
                      <p className="text-sm">{testResult.intent}</p>
                    </div>
                  )}

                  {testResult.suggested_action && (
                    <div>
                      <Label className="text-xs text-slate-500">Suggested Action</Label>
                      <p className="text-sm">{testResult.suggested_action}</p>
                    </div>
                  )}

                  {testResult.error && (
                    <div className="p-2 bg-red-50 rounded">
                      <p className="text-sm text-red-600">{testResult.error}</p>
                    </div>
                  )}

                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-500">Raw Response</summary>
                    <pre className="mt-2 p-2 bg-slate-100 rounded overflow-x-auto">
                      {testResult.raw_response}
                    </pre>
                  </details>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Conflicts Tab */}
        <TabsContent value="conflicts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-amber-600" />
                    Lead Conflicts
                  </CardTitle>
                  <CardDescription>Messages identified as leads but a lead already exists for the phone number</CardDescription>
                </div>
                <Button variant="outline" onClick={fetchConflicts} disabled={conflictsLoading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${conflictsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {conflicts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-200" />
                  <p>No conflicts found</p>
                  <p className="text-sm">All incoming leads are being processed normally</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {conflicts.map((conflict) => (
                    <div key={conflict.id} className={`border rounded-lg p-4 ${conflict.status === 'pending' ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={
                              conflict.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              conflict.status === 'merged' ? 'bg-blue-100 text-blue-700' :
                              conflict.status === 'ignored' ? 'bg-slate-100 text-slate-700' :
                              'bg-emerald-100 text-emerald-700'
                            }>
                              {conflict.status?.toUpperCase()}
                            </Badge>
                            <span className="text-sm text-slate-500">{conflict.id}</span>
                            <span className="text-xs text-slate-400">{new Date(conflict.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            {/* Incoming Message */}
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <h4 className="text-xs font-medium text-blue-700 mb-2">📱 INCOMING MESSAGE</h4>
                              <div className="space-y-1 text-sm">
                                <p><span className="text-slate-500">Phone:</span> <strong>{conflict.incoming_phone}</strong></p>
                                <p><span className="text-slate-500">Name:</span> {conflict.incoming_name || '-'}</p>
                                <p><span className="text-slate-500">Company:</span> {conflict.incoming_company || 'Organic'}</p>
                                <p className="text-xs mt-2 italic text-slate-600 line-clamp-2">&ldquo;{conflict.incoming_message}&rdquo;</p>
                                <div className="mt-2">
                                  <Badge variant="outline" className="text-xs">AI Confidence: {conflict.ai_confidence}%</Badge>
                                </div>
                              </div>
                            </div>
                            
                            {/* Existing Lead */}
                            <div className="bg-emerald-50 p-3 rounded-lg">
                              <h4 className="text-xs font-medium text-emerald-700 mb-2">📋 EXISTING LEAD</h4>
                              <div className="space-y-1 text-sm">
                                <p><span className="text-slate-500">Lead ID:</span> <code className="text-xs bg-slate-200 px-1 rounded">{conflict.existing_lead_id}</code></p>
                                <p><span className="text-slate-500">Company:</span> {conflict.existing_lead_company}</p>
                                <p><span className="text-slate-500">Contact:</span> {conflict.existing_lead_contact}</p>
                                <p><span className="text-slate-500">Status:</span> <Badge variant="outline" className="text-xs">{conflict.existing_lead_status}</Badge></p>
                                <p><span className="text-slate-500">Source:</span> {conflict.existing_lead_source}</p>
                              </div>
                            </div>
                          </div>
                          
                          {conflict.ai_reason && (
                            <p className="mt-2 text-xs text-slate-500">
                              <span className="font-medium">AI Reason:</span> {conflict.ai_reason}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {conflict.status === 'pending' && (
                        <div className="mt-4 pt-3 border-t flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => resolveConflict(conflict.id, 'merged', 'Merged with existing lead')}>
                            Merge with Lead
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => resolveConflict(conflict.id, 'ignored', 'Ignored - duplicate')}>
                            Ignore
                          </Button>
                          <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => resolveConflict(conflict.id, 'new_lead_created', 'Created as separate lead')}>
                            Create New Lead
                          </Button>
                        </div>
                      )}
                      
                      {conflict.resolved_at && (
                        <p className="mt-2 text-xs text-slate-400">
                          Resolved: {new Date(conflict.resolved_at).toLocaleString()} {conflict.resolution_notes && `- ${conflict.resolution_notes}`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Webhook Detail Modal */}
      <Dialog open={!!selectedWebhook} onOpenChange={() => setSelectedWebhook(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Message Details</DialogTitle>
            <DialogDescription>
              ID: {selectedWebhook?.webhook?.id}
            </DialogDescription>
          </DialogHeader>

          {selectedWebhook && (
            <div className="space-y-4">
              {/* Webhook Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <p className="font-mono">{selectedWebhook.webhook.phone}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Type</Label>
                  <p>{selectedWebhook.webhook.message_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Received</Label>
                  <p>{new Date(selectedWebhook.webhook.received_at).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Badge className={
                    selectedWebhook.webhook.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    selectedWebhook.webhook.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }>
                    {selectedWebhook.webhook.status}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Message Preview</Label>
                <p className="p-2 bg-slate-50 rounded">{selectedWebhook.webhook.message_preview}</p>
              </div>

              {/* AI Analysis */}
              {selectedWebhook.analysis && (
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    AI Analysis Result
                  </h4>

                  <div className="flex items-center gap-4 flex-wrap">
                    <Badge className={selectedWebhook.analysis.is_lead ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                      {selectedWebhook.analysis.is_lead ? 'LEAD' : 'NOT A LEAD'}
                    </Badge>
                    <span>Confidence: <strong>{selectedWebhook.analysis.confidence}%</strong></span>
                    <span className="text-sm text-slate-500">
                      Model: {selectedWebhook.analysis.model_used}
                    </span>
                  </div>

                  {/* Token Usage */}
                  {(selectedWebhook.analysis.total_tokens || selectedWebhook.analysis.input_tokens || selectedWebhook.analysis.output_tokens) && (
                    <div className="flex items-center gap-4 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                      <Zap className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-700 dark:text-blue-300">Token Usage:</span>
                      {selectedWebhook.analysis.input_tokens && (
                        <span className="text-blue-600 dark:text-blue-400">
                          Input: <strong>{selectedWebhook.analysis.input_tokens}</strong>
                        </span>
                      )}
                      {selectedWebhook.analysis.output_tokens && (
                        <span className="text-blue-600 dark:text-blue-400">
                          Output: <strong>{selectedWebhook.analysis.output_tokens}</strong>
                        </span>
                      )}
                      {selectedWebhook.analysis.total_tokens && (
                        <span className="text-blue-700 dark:text-blue-300 font-medium">
                          Total: <strong>{selectedWebhook.analysis.total_tokens}</strong>
                        </span>
                      )}
                      {selectedWebhook.analysis.processing_time_ms && (
                        <span className="text-slate-500">
                          ({selectedWebhook.analysis.processing_time_ms}ms)
                        </span>
                      )}
                    </div>
                  )}

                  {selectedWebhook.analysis.reason && (
                    <div>
                      <Label className="text-xs text-slate-500">Reason</Label>
                      <p className="text-sm">{selectedWebhook.analysis.reason}</p>
                    </div>
                  )}

                  {selectedWebhook.analysis.intent && (
                    <div>
                      <Label className="text-xs text-slate-500">Intent</Label>
                      <p className="text-sm">{selectedWebhook.analysis.intent}</p>
                    </div>
                  )}

                  {selectedWebhook.analysis.suggested_action && (
                    <div>
                      <Label className="text-xs text-slate-500">Suggested Action</Label>
                      <p className="text-sm">{selectedWebhook.analysis.suggested_action}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Raw Payload */}
              <details>
                <summary className="cursor-pointer text-sm text-slate-500">View Raw Webhook Payload</summary>
                <pre className="mt-2 p-2 bg-slate-100 rounded text-xs overflow-x-auto max-h-48">
                  {JSON.stringify(selectedWebhook.webhook.payload, null, 2)}
                </pre>
              </details>

              {/* Audit Logs */}
              {selectedWebhook.audit_logs?.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500">Audit Trail</Label>
                  <div className="space-y-1 mt-1">
                    {selectedWebhook.audit_logs.map((log) => (
                      <div key={log.id} className="text-xs p-2 bg-slate-50 rounded flex justify-between">
                        <span>{log.action}</span>
                        <span className="text-slate-500">{new Date(log.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedWebhook(null)}>
              Close
            </Button>
            {selectedWebhook && (
              <Button onClick={() => { reprocessWebhook(selectedWebhook.webhook.id); setSelectedWebhook(null); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reprocess
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Config Dialog */}
      <Dialog open={showSendConfigDialog} onOpenChange={setShowSendConfigDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSendConfig ? 'Edit' : 'Add'} WhatsApp Send Configuration</DialogTitle>
            <DialogDescription>
              Configure your WhatsApp Business API endpoint for sending messages.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Configuration Name *</Label>
                <Input
                  value={sendConfigForm.name}
                  onChange={(e) => setSendConfigForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Production WhatsApp"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Input
                  value={sendConfigForm.description}
                  onChange={(e) => setSendConfigForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">API Configuration</h4>
              
              <div className="space-y-4">
                <div>
                  <Label>API URL *</Label>
                  <Input
                    value={sendConfigForm.api_url}
                    onChange={(e) => setSendConfigForm(f => ({ ...f, api_url: e.target.value }))}
                    placeholder="https://graph.facebook.com/v21.0/{phone_number_id}/messages"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    For Meta Cloud API: https://graph.facebook.com/v21.0/YOUR_PHONE_NUMBER_ID/messages
                  </p>
                </div>

                <div>
                  <Label>Phone Number ID</Label>
                  <Input
                    value={sendConfigForm.phone_number_id}
                    onChange={(e) => setSendConfigForm(f => ({ ...f, phone_number_id: e.target.value }))}
                    placeholder="e.g., 123456789012345"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Found in Meta Developer Console → WhatsApp → API Setup
                  </p>
                </div>

                <div>
                  <Label>Access Token (Bearer Token)</Label>
                  <div className="flex gap-2">
                    <Input
                      type={showAccessToken ? 'text' : 'password'}
                      value={sendConfigForm.access_token}
                      onChange={(e) => setSendConfigForm(f => ({ ...f, access_token: e.target.value }))}
                      placeholder={editingSendConfig ? '••••••• (leave blank to keep existing)' : 'Enter access token'}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setShowAccessToken(!showAccessToken)}
                    >
                      {showAccessToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Permanent token from Meta Developer Console. Will be sent as Authorization: Bearer TOKEN
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Custom Headers</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addHeader}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Header
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {sendConfigForm.headers.map((header, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Input
                          placeholder="Header Name (e.g., X-API-Key)"
                          value={header.key}
                          onChange={(e) => updateHeader(index, 'key', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Header Value"
                          value={header.value}
                          onChange={(e) => updateHeader(index, 'value', e.target.value)}
                          className="flex-1"
                        />
                        {sendConfigForm.headers.length > 1 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeHeader(index)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Additional headers to send with API requests. Content-Type is always set automatically.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">AI Response Settings</h4>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable AI Auto-Response</Label>
                    <p className="text-xs text-slate-500">Automatically send AI-generated responses to incoming messages</p>
                  </div>
                  <Switch
                    checked={sendConfigForm.enable_ai_response}
                    onCheckedChange={(checked) => setSendConfigForm(f => ({ ...f, enable_ai_response: checked }))}
                  />
                </div>

                {sendConfigForm.enable_ai_response && (
                  <div>
                    <Label>Response Delay (seconds)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="30"
                      value={sendConfigForm.ai_response_delay_seconds}
                      onChange={(e) => setSendConfigForm(f => ({ ...f, ai_response_delay_seconds: parseInt(e.target.value) || 2 }))}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Delay before sending AI response (makes it feel more natural)
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Set as Default</Label>
                    <p className="text-xs text-slate-500">Use this config when no specific config is specified</p>
                  </div>
                  <Switch
                    checked={sendConfigForm.is_default}
                    onCheckedChange={(checked) => setSendConfigForm(f => ({ ...f, is_default: checked }))}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSendConfigDialog(false); setEditingSendConfig(null); }}>
              Cancel
            </Button>
            <Button onClick={saveSendConfig} disabled={saving.sendConfig || !sendConfigForm.name || !sendConfigForm.api_url}>
              {saving.sendConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingSendConfig ? 'Update' : 'Create'} Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppAIPage;
